import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  Sparkles,
  ArrowRight,
  Mail,
  Copy,
  Check,
} from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import {
  pollCheckoutStatus,
  saveEntitlement,
  saveTokenForRecovery,
  fetchConfig,
  fetchReportByToken,
  buildPdfDownloadUrl,
} from "../lib/pro";
import { toast } from "sonner";
import SiteFootnote from "../components/SiteFootnote";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const [entitlement, setEntitlement] = useState(null);
  // Once polling succeeds, prefetch the report into sessionStorage so
  // /report renders without an extra round-trip — and capture the
  // report_id so we can offer an immediate PDF download right here.
  const [recoveredReportId, setRecoveredReportId] = useState(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    document.title = "Payment confirmed — CFO Grade";
    if (!sessionId) {
      setState({ status: "error", message: "Missing session_id." });
      return;
    }
    // Reset on (re)mount — StrictMode in dev runs the cleanup once before the
    // real run, leaving stoppedRef.current=true and breaking polling.
    stoppedRef.current = false;
    let attempts = 0;
    let timer;
    const tick = async () => {
      if (stoppedRef.current) return;
      attempts += 1;
      setState((s) => ({ ...s, attempts }));
      try {
        const data = await pollCheckoutStatus(sessionId);
        if (data.payment_status === "paid" && data.entitlement) {
          stoppedRef.current = true;
          saveEntitlement(data.entitlement);
          setEntitlement(data.entitlement);
          setState({ status: "paid" });
          // Recovery: prefetch the report into sessionStorage and surface
          // the report_id so a direct "Download PDF" button can render
          // here. Failure is non-fatal — `/report` will retry the same
          // recovery on its own mount.
          const reportId =
            data.entitlement.report_id ||
            data.entitlement.metadata?.report_id ||
            null;
          const token = data.entitlement.token;
          if (reportId && token) {
            try {
              const res = await fetchReportByToken(reportId, token);
              if (res?.report) {
                sessionStorage.setItem(
                  "ll_last_report",
                  JSON.stringify(res.report)
                );
                setRecoveredReportId(reportId);
              }
            } catch {
              /* non-fatal */
            }
          }
          return;
        }
        if (data.status === "expired") {
          stoppedRef.current = true;
          setState({ status: "expired" });
          return;
        }
      } catch (e) {
        // 404 = unknown session_id — short-circuit instead of polling 15 times
        if (e?.response?.status === 404) {
          stoppedRef.current = true;
          setState({ status: "error", message: "Unknown payment session." });
          return;
        }
        // Otherwise log and retry
        console.warn("payment status poll error:", e?.response?.status || e?.message);
      }
      if (attempts >= MAX_ATTEMPTS) {
        stoppedRef.current = true;
        setState({ status: "timeout" });
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => {
      stoppedRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  const isPolling = state.status === "polling";
  const isPaid = state.status === "paid";
  const isError = state.status === "error" || state.status === "expired" || state.status === "timeout";

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative" data-testid="payment-success-page">
      <header className="max-w-5xl mx-auto px-6 sm:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <BrandWordmark size="md" />
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        {isPolling && (
          <div className="text-center" data-testid="payment-polling">
            <Loader2 size={36} strokeWidth={1.5} className="text-[#C26D5C] mx-auto animate-spin" />
            <h1 className="font-serif-display text-4xl text-[#1F382E] mt-6 tracking-tight">
              Confirming your payment…
            </h1>
            <p className="text-[#5C665D] mt-3 leading-relaxed">
              Stripe is finalising the transaction. This usually takes a few seconds.
            </p>
            <p className="text-xs text-[#5C665D] mt-6 uppercase tracking-[0.18em]">
              Attempt {state.attempts} of {MAX_ATTEMPTS}
            </p>
          </div>
        )}

        {isPaid && (
          <div className="text-center" data-testid="payment-confirmed">
            <CheckCircle2 size={44} strokeWidth={1.5} className="text-[#2C5C43] mx-auto" />
            <h1 className="font-serif-display text-5xl text-[#1F382E] mt-6 tracking-tight">
              You're unlocked.
            </h1>
            <p className="text-[#5C665D] mt-4 leading-relaxed">
              {entitlement?.scope === "pro"
                ? "Pro Pass is active on this device for 30 days. AI memos and unlimited PDF exports are now enabled."
                : "Single PDF unlock is locked to this report and never expires on this device."}
            </p>

            <div className="mt-8 bg-white border border-[#E5E0D8] rounded-xl p-6 text-left">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
                <Sparkles size={12} strokeWidth={1.5} className="text-[#C26D5C]" />
                Your entitlement
              </div>
              <p className="font-serif-display text-lg text-[#1F382E] mt-1">
                {entitlement?.scope === "pro" ? "Pro Pass" : "Single PDF unlock"}
              </p>
              {entitlement?.expires_at && (
                <p className="text-xs text-[#5C665D] mt-1">
                  Active until {new Date(entitlement.expires_at).toLocaleString()}
                </p>
              )}
              {!entitlement?.expires_at && (
                <p className="text-xs text-[#5C665D] mt-1">Never expires.</p>
              )}
            </div>

            <SaveProPanel entitlement={entitlement} />

            {/* Immediate PDF download once recovery has prefetched the report */}
            {recoveredReportId && entitlement?.token && (
              <div
                className="mt-6 bg-white border border-[#E5E0D8] rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-3"
                data-testid="payment-pdf-callout"
              >
                <p className="text-sm text-[#1F382E] sm:text-left">
                  Want a copy for your records?{" "}
                  <span className="text-[#5C665D]">PDF includes everything in your report.</span>
                </p>
                <a
                  href={buildPdfDownloadUrl(recoveredReportId, entitlement.token)}
                  download
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#1F382E] hover:text-[#C26D5C] border border-[#1F382E] hover:border-[#C26D5C] rounded-md px-4 py-2 transition-all duration-300 shrink-0"
                  data-testid="payment-download-pdf-btn"
                >
                  <ArrowRight size={14} strokeWidth={1.5} className="rotate-90" />
                  Download PDF
                </a>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => navigate("/report")}
                className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300"
                data-testid="payment-back-to-report-btn"
              >
                Back to my report
                <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
              </Button>
              <Link to="/analyze">
                <Button
                  variant="outline"
                  className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300"
                >
                  Run another analysis
                </Button>
              </Link>
            </div>
          </div>
        )}

        {isError && (
          <div className="text-center" data-testid="payment-error">
            <AlertCircle size={36} strokeWidth={1.5} className="text-[#A84632] mx-auto" />
            <h1 className="font-serif-display text-4xl text-[#1F382E] mt-6 tracking-tight">
              Couldn't confirm the payment.
            </h1>
            <p className="text-[#5C665D] mt-3 leading-relaxed">
              {state.status === "expired"
                ? "The Stripe session expired before we could confirm. No charge was made."
                : state.status === "timeout"
                  ? "We couldn't get a response in time. If your card was charged, refresh in a minute — your unlock will activate automatically."
                  : state.message || "Something went wrong."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => window.location.reload()}
                className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6"
              >
                Try again
              </Button>
              <Link to="/report">
                <Button variant="ghost" className="text-[#5C665D]">
                  Back to report
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>
      <SiteFootnote />
    </div>
  );
}

function SaveProPanel({ entitlement }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchConfig()
      .then((cfg) => !cancelled && setEmailEnabled(cfg.emailEnabled))
      .catch(() => !cancelled && setEmailEnabled(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const restoreUrl =
    typeof window !== "undefined" && entitlement?.token
      ? `${window.location.origin}/pay/restore?token=${entitlement.token}`
      : "";

  async function handleCopy() {
    if (!restoreUrl) return;
    try {
      await navigator.clipboard.writeText(restoreUrl);
      setCopied(true);
      toast.success("Restore link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Long-press the link to select it manually.");
    }
  }

  async function handleEmail(e) {
    e.preventDefault();
    if (!email || !entitlement?.token) return;
    setBusy(true);
    try {
      const res = await saveTokenForRecovery({ token: entitlement.token, email });
      setDone(res);
      if (res.email_sent) {
        toast.success("Restore link emailed.");
      } else if (!res.email_enabled) {
        toast.message("Email isn't configured yet — your link is saved against this address.");
      } else {
        toast.error("We saved your email but the message didn't go out. Try again in a moment.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!entitlement?.token) return null;

  const isProPass = entitlement?.scope === "pro";
  const recoveryHelp = "If you lose this link before your pass expires, contact us through the in-app feedback widget — we'll get you back in.";

  return (
    <div
      className="mt-6 bg-white border-2 border-[#9c6b1d]/40 rounded-xl p-6 sm:p-7 text-left shadow-[0_4px_16px_rgba(156,107,29,0.06)]"
      data-testid="save-pro-panel"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#9c6b1d] font-medium">
        <Mail size={12} strokeWidth={2} className="text-[#9c6b1d]" />
        Save this — it's how you come back
      </div>
      <p className="font-serif-display text-2xl sm:text-3xl text-[#1F382E] mt-2 tracking-tight leading-tight">
        Bookmark or copy your restore link now.
      </p>
      <p className="text-[13.5px] text-[#1F382E] mt-2 leading-relaxed">
        {isProPass
          ? "This is the only way to get back into your Pro Pass on another device — or if you clear your browser. We do not send recovery emails automatically."
          : "This is the only way to get back to your PDF unlock on another device — or if you clear your browser. We do not send recovery emails automatically."}
      </p>
      <p className="text-[12.5px] text-[#5C665D] mt-2 leading-relaxed">
        Save it in your password manager, bookmark it, or paste it into a note you'll find later.
      </p>

      <div className="mt-4 flex items-stretch gap-2">
        <input
          readOnly
          value={restoreUrl}
          onFocus={(e) => e.target.select()}
          className="flex-1 bg-[#F7F5F0] border border-[#E5E0D8] rounded-md text-xs text-[#1F382E] px-3 py-2.5 font-mono truncate focus:outline-none focus:ring-2 focus:ring-[#1F382E]"
          data-testid="restore-url-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleCopy}
          className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] transition-all duration-300 h-auto px-4"
          data-testid="restore-url-copy-btn"
        >
          {copied ? (
            <>
              <Check size={14} strokeWidth={2} className="mr-1.5" /> Copied
            </>
          ) : (
            <>
              <Copy size={14} strokeWidth={1.5} className="mr-1.5" /> Copy link
            </>
          )}
        </Button>
      </div>

      <label
        className="mt-4 flex items-start gap-2.5 text-[12.5px] text-[#1F382E] cursor-pointer select-none"
        data-testid="restore-url-ack-label"
      >
        <input
          type="checkbox"
          checked={ack}
          onChange={(e) => setAck(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-[#E5E0D8] text-[#1F382E] focus:ring-[#1F382E]"
          data-testid="restore-url-ack-checkbox"
        />
        <span>
          I've saved my restore link (bookmark / password manager / note). I understand
          this is the only way to recover my unlock if I switch devices or clear my browser.
        </span>
      </label>

      <p className="text-[11.5px] text-[#5C665D] mt-3 leading-relaxed border-t border-[#F0EDE5] pt-3">
        {recoveryHelp}
      </p>
    </div>
  );
}
